import React, { useEffect, useRef, useState } from "react";
import { Modal, Platform, Pressable, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Text from "@/components/shared/AppText";
import analytics from "@/analytics";
import { t, tRich } from "@/localization";
import tw from "twrnc";

// The "picker" paywall in the paywall test (docs/paywall-test.md): one line,
// "Replay online for 1 year ⌄", above the Unlock button. Tapping it opens a
// sheet with every length and its price; picking one goes straight to checkout
// (PayWall's onSelect, like a tier card) and the line, feature row and button
// show that length if the buyer comes back.

const COLORS = {
  ink: "#0F2033",
  muted: "#6B7A8A",
  line: "#E6ECF2",
  coral: "#F05A3C",
  coralSoft: "#FFF2ED",
};

// Shortest first in the sheet.
const SHEET_ORDER = [
  "events_storage_1m",
  "events_storage_1y",
  "events_storage_10y",
];

export default function PaywallDurationPicker({
  tiers,
  selectedProductId,
  onSelect,
  testId,
}) {
  const [open, setOpen] = useState(false);
  // iOS can't present the checkout (Safari or the App Store sheet) while
  // this sheet is still sliding away: the choice waits for onDismiss, or a
  // timer if that never comes.
  const pending = useRef(null);
  const pendingTimer = useRef(null);
  useEffect(() => () => clearTimeout(pendingTimer.current), []);
  const deliver = () => {
    clearTimeout(pendingTimer.current);
    const productId = pending.current;
    pending.current = null;
    if (productId) onSelect(productId);
  };
  const insets = useSafeAreaInsets();
  const selected =
    tiers.find((tier) => tier.productId === selectedProductId) || tiers[0];
  const options = SHEET_ORDER.map((id) =>
    tiers.find((tier) => tier.productId === id)
  ).filter(Boolean);

  const openSheet = () => {
    setOpen(true);
    analytics.track("Paywall Picker Opened", {
      test_id: testId,
      selected: selected?.productId,
    });
  };

  const choose = (tier) => {
    setOpen(false);
    if (tier.productId !== selected?.productId) {
      analytics.track("Paywall Duration Changed", {
        test_id: testId,
        from: selected?.productId,
        to: tier.productId,
      });
    }
    if (Platform.OS !== "ios") {
      onSelect(tier.productId);
      return;
    }
    pending.current = tier.productId;
    clearTimeout(pendingTimer.current);
    pendingTimer.current = setTimeout(deliver, 800);
  };

  return (
    <>
      <Pressable
        onPress={openSheet}
        accessibilityRole="button"
        accessibilityLabel={t("Replay online for {length}. Change how long", {
          length: selected?.lengthLabel,
        })}
        hitSlop={8}
        style={tw`flex-row items-center justify-center py-1.5`}
        nativeID="paywall-duration-picker"
      >
        <Text style={[tw`text-[14px]`, { color: COLORS.muted }]}>
          {tRich("Replay online for <b>{length}</b>", {
            length: selected?.lengthLabel,
            b: (words) => (
              <Text
                style={[
                  tw`text-[14px] font-bold`,
                  {
                    color: COLORS.ink,
                    textDecorationLine: "underline",
                    textDecorationStyle: "dotted",
                    textDecorationColor: COLORS.muted,
                  },
                ]}
              >
                {words}
              </Text>
            ),
          })}
        </Text>
        <Ionicons
          name="chevron-down"
          size={14}
          color={COLORS.muted}
          style={tw`ml-1`}
        />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="slide"
        onRequestClose={() => setOpen(false)}
        onDismiss={deliver}
      >
        <View style={tw`flex-1 justify-end`}>
          <Pressable
            onPress={() => setOpen(false)}
            accessibilityRole="button"
            accessibilityLabel={t("Close")}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: "rgba(15,32,51,0.45)",
            }}
          />
          <View
            style={[
              tw`bg-white rounded-t-3xl px-5 pt-2.5 w-full self-center`,
              {
                maxWidth: 640,
                paddingBottom: Math.max(insets.bottom, 12) + 12,
              },
            ]}
            nativeID="paywall-duration-sheet"
          >
            <View
              style={[
                tw`self-center rounded-full mb-3`,
                { width: 38, height: 4, backgroundColor: COLORS.line },
              ]}
            />
            <Text
              style={[tw`text-[16px] font-bold mb-2`, { color: COLORS.ink }]}
            >
              {t("Keep the replay online for")}
            </Text>
            {options.map((tier) => {
              const isSelected = tier.productId === selected?.productId;
              return (
                <Pressable
                  key={tier.productId}
                  onPress={() => choose(tier)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSelected }}
                  style={[
                    tw`flex-row items-center py-3 px-1`,
                    { borderTopWidth: 1, borderTopColor: COLORS.line },
                  ]}
                >
                  <View style={tw`flex-1 min-w-0 mr-3`}>
                    <Text
                      style={[tw`text-[15px] font-bold`, { color: COLORS.ink }]}
                    >
                      {tier.lengthLabel}
                    </Text>
                    {!!tier.untilLabel && (
                      <Text
                        style={[
                          tw`text-[12px] mt-0.5`,
                          { color: COLORS.muted },
                        ]}
                      >
                        {tier.untilLabel}
                      </Text>
                    )}
                  </View>
                  <Text
                    style={[
                      tw`text-[16px] font-bold`,
                      { color: isSelected ? COLORS.coral : COLORS.ink },
                    ]}
                  >
                    {tier.localizedPrice || "--"}
                  </Text>
                  <View style={[tw`ml-2 items-center`, { width: 20 }]}>
                    {isSelected && (
                      <Ionicons
                        name="checkmark"
                        size={18}
                        color={COLORS.coral}
                      />
                    )}
                  </View>
                </Pressable>
              );
            })}
            <Text
              style={[
                tw`text-[12px] mt-1`,
                { color: COLORS.muted },
                Platform.OS === "web" && { userSelect: "none" },
              ]}
            >
              {t("You can keep it online longer later, from the event page.")}
            </Text>
          </View>
        </View>
      </Modal>
    </>
  );
}
